/**
 * 매뉴얼 단계별 안내 엔진(service-manual 「단계별 안내 플레이어」) — 실제 앱 화면 그림 위로 커서가 움직여 누르고 다음 화면으로
 * 넘어간다. 그림 바꾸기·커서·강조 상자·확대·진행 막대는 DOM 에 직접 쓰고, 단계 번호·재생 여부·첫 그림 준비처럼 드물게 바뀌는
 * 값만 콜백으로 알린다.
 *
 * 불러오기: 첫 그림이 그려지기 전에는 무대 바탕의 흐린 미리보기만 보인다(그림·커서·강조 상자는 숨김 — 흰 화면·왼쪽 위 커서 금지).
 * 지금 보여 줄 그림은 높은 우선순위로, 재생 중에는 다음 두 장만 미리 받아 해독까지 마친 뒤 바꾼다(바꿀 때 빈칸·깜빡임 없게).
 * 그림 폭은 무대 폭 × 화면 픽셀 비율 × 그 단계 확대 배율을 덮는 가장 작은 것이다.
 */
import {
  GUIDE_SCENE,
  type GuideAction,
  type GuideFrame,
  type GuideRect,
  type ManualGuide,
} from "./manualGuides";
import { SITE_QUERY } from "src/domains/shared/constants/styles/site";
import { truncateNumber } from "src/domains/shared/utils/truncateNumber";

type Point = readonly [x: number, y: number];

/** 앞 단계에서 누른 자리가 없을 때 커서가 쉬는 곳(장면 %) — 위치 없이 왼쪽 위에 박히지 않게 */
const CURSOR_REST: Point = [62, 86];
const MAX_ZOOM = 2.6;
/** 두 확대 영역이 이만큼 겹치면 앞 영역을 그대로 둔다(비슷한 영역 사이에서 화면이 흔들리지 않게) */
const KEEP_VIEW_OVERLAP = 0.7;
const CROSSFADE_MS = 260;
const SWAP_SETTLE_MS = 40;
const LOAD_TRIES = 3;
const RETRY_GAP_MS = 600;
const MAX_PIXEL_RATIO = 3;
/** 한 단계의 박자(ms): 보기 → 커서 이동 → 강조 → 누름 → (끌기) → 머묾 → 다음 */
const BEAT = {
  look: 900,
  move: 700,
  ring: 380,
  press: 140,
  /** 더블클릭의 두 누름 사이에 뗀 시간 */
  doubleGap: 120,
  drag: 800,
  typeHold: 500,
  clickHold: 260,
  next: 330,
  /** 누름 없이 확대해 결과만 보여 주는 중간 단계 — 바뀐 값을 읽을 시간 */
  review: 1400,
  last: 2200,
} as const;

export interface GuideEngineElements {
  stage: HTMLElement;
  /** 그림·강조 상자·커서를 한 층에 두고 확대하는 층 */
  view: HTMLElement;
  image: HTMLImageElement;
  ring: HTMLElement;
  cursor: HTMLElement;
  flash: HTMLElement;
  /** 단계 막대의 차오르는 칸(단계 순서) */
  bars: () => HTMLElement[];
}

export interface GuideEngineOptions {
  elements: GuideEngineElements;
  guide: ManualGuide;
  /** 단계 그림 주소 */
  frameUrl: (index: number, width: number) => string;
  reducedMotion: boolean;
  /** 처음 보여 줄 단계(0부터) — 주소로 단계를 지정해 연 절. 없으면 첫 단계 */
  startIndex?: number;
  onStep: (index: number) => void;
  onPlaying: (playing: boolean) => void;
  onReady: () => void;
}

export interface GuideEngine {
  /** 화면에 들어오고 나갈 때 — 들어오면 자동 재생(직접 멈췄거나 움직임 줄이기면 지금 단계 화면만) */
  setVisible(visible: boolean): void;
  /** 화면 가까이 왔을 때 첫 그림을 미리 받는다 */
  prefetch(): void;
  /** 그 단계부터 다시 재생(움직임 줄이기면 그 단계 화면만) */
  seek(index: number): void;
  seekBy(delta: number): void;
  /** 재생 ↔ 일시정지. `fromStage` 면 무대 가운데 잠깐 표시가 뜬다 */
  toggle(fromStage: boolean): void;
  /** 단계 미리보기 그림 주소 — 받아 둔 그림이 있으면 그것을, 없으면 가장 작은 폭 */
  peekUrl(index: number): string;
  dispose(): void;
}

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));
const nextPaint = () =>
  new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/** 장면 px 사각형 → 장면 % [x, y, w, h] */
const toPercent = ([x, y, w, h]: GuideRect) =>
  [
    (x / GUIDE_SCENE.width) * 100,
    (y / GUIDE_SCENE.height) * 100,
    (w / GUIDE_SCENE.width) * 100,
    (h / GUIDE_SCENE.height) * 100,
  ] as const;

const centerOf = (rect: GuideRect): Point => {
  const [x, y, w, h] = toPercent(rect);
  return [x + w / 2, y + h / 2];
};

const overlapRatio = (a: GuideRect, b: GuideRect) => {
  const ix = Math.max(
    0,
    Math.min(a[0] + a[2], b[0] + b[2]) - Math.max(a[0], b[0])
  );
  const iy = Math.max(
    0,
    Math.min(a[1] + a[3], b[1] + b[3]) - Math.max(a[1], b[1])
  );
  const intersection = ix * iy;
  const union = a[2] * a[3] + b[2] * b[3] - intersection;
  return union > 0 ? intersection / union : 0;
};

/**
 * 영역(장면 px)을 무대에 꽉 차게(최대 2.6배) 놓는 배율·이동. 그림 층은 무대 폭·장면 비율(16:10)이라 무대가 세로여도 찌그러지지
 * 않고, 확대한 장면이 무대를 덮을 수 있으면 빈 띠가 생기지 않게 가둔다. 영역이 없으면 장면 전체를 무대 안에 담는다.
 */
export function fitView(
  view: GuideRect | null,
  stageWidth: number,
  stageHeight: number
) {
  const baseScale = stageWidth / GUIDE_SCENE.width;
  const sceneHeight = GUIDE_SCENE.height * baseScale;
  if (!view) {
    const scale = Math.min(1, stageHeight / sceneHeight);
    return {
      scale,
      x: (stageWidth - stageWidth * scale) / 2,
      y: (stageHeight - sceneHeight * scale) / 2,
    };
  }
  const [vx, vy, vw, vh] = view.map((v) => v * baseScale);
  const scale = Math.min(stageWidth / vw, stageHeight / vh, MAX_ZOOM);
  const width = stageWidth * scale;
  const height = sceneHeight * scale;
  const x = (stageWidth - vw * scale) / 2 - vx * scale;
  const y = (stageHeight - vh * scale) / 2 - vy * scale;
  return {
    scale,
    x:
      width >= stageWidth
        ? Math.min(0, Math.max(stageWidth - width, x))
        : (stageWidth - width) / 2,
    y:
      height >= stageHeight
        ? Math.min(0, Math.max(stageHeight - height, y))
        : (stageHeight - height) / 2,
  };
}

/** 필요한 폭을 덮는 가장 작은 그림 폭(없으면 가장 큰 것) */
export const pickFrameWidth = (widths: readonly number[], need: number) =>
  widths.find((width) => width >= need) ?? widths[widths.length - 1];

/** 누름 없이 확대만 하는 중간 단계(마지막 단계 제외) — `BEAT.review` 만큼 더 머문다 */
const isReviewStep = (frame: GuideFrame, isLast: boolean) =>
  !frame.act && frame.view !== undefined && !isLast;

/** 굴리기 방향 — 굴리기 단계가 아니면 없음, 위로 굴리기가 아니면 아래 */
const scrollDirection = (action: GuideAction) => {
  if (action.kind !== "scroll") return null;
  return action.dir === "up" ? ("up" as const) : ("down" as const);
};

/** 단계 동작을 커서 표시로 — 굴리기 방향(굴리기만), 누름 횟수(더블클릭은 두 번), 오른쪽 버튼 표시 여부 */
export function cursorGesture(action: GuideAction) {
  return {
    scroll: scrollDirection(action),
    presses: action.kind === "dblclick" ? 2 : 1,
    rightButton: action.button === "right",
  };
}

/** 누름 시간 — 더블클릭은 두 누름과 그 사이 뗀 시간 */
const pressTime = (presses: number) =>
  BEAT.press * presses + BEAT.doubleGap * (presses - 1);

/** 한 단계에 걸리는 시간 — 재생 순서와 같은 계산이고, 지금 칸이 이 시간에 맞춰 차오른다 */
export function stepDuration(frame: GuideFrame, isLast: boolean) {
  const action = frame.act;
  const acting = action
    ? BEAT.move +
      BEAT.ring +
      pressTime(cursorGesture(action).presses) +
      (action.to || action.kind === "scroll" ? BEAT.drag : 0) +
      (action.kind === "type" ? BEAT.typeHold : BEAT.clickHold)
    : 0;
  const review = isReviewStep(frame, isLast) ? BEAT.review : 0;
  return BEAT.look + acting + review + (isLast ? BEAT.last : BEAT.next);
}

export function createGuideEngine({
  elements,
  guide,
  frameUrl,
  reducedMotion,
  startIndex = 0,
  onStep,
  onPlaying,
  onReady,
}: GuideEngineOptions): GuideEngine {
  const { stage, view, image, ring, cursor, flash } = elements;
  const frames = guide.frames;
  const count = frames.length;
  // 휴대폰 세로 무대(4:5)로 바뀌는 너비 — 이하에서는 단계마다 미리 고른 세로 영역(`mv`)을 보여 준다.
  // 가로로 둔 터치 화면은 좁아도 16:10 무대라 세로 영역을 쓰지 않는다. 두 경계 모두 무대 스타일과 같은 토큰이다
  const compactQuery = window.matchMedia(SITE_QUERY.compact);
  const landscapeTouchQuery = window.matchMedia(SITE_QUERY.landscapeTouch);
  const loaded = new Map<
    number,
    { url: string; image: Promise<HTMLImageElement | null> }
  >();

  let current = Math.max(0, Math.min(count - 1, startIndex));
  /** 재생 회차 — 더 새 조작이 끼어들면 늦게 끝난 옛 전환이 화면을 덮어쓰지 않게 멈춘다 */
  let token = 0;
  let playing = false;
  let userPaused = false;
  let ready = false;
  let disposed = false;
  let scale = 1;
  let lastView: GuideRect | null = null;
  let cursorAt: Point = CURSOR_REST;
  let sleepTimer = 0;
  let wake: (() => void) | null = null;
  let fill: Animation | null = null;

  const viewOf = (frame: GuideFrame) =>
    compactQuery.matches && !landscapeTouchQuery.matches
      ? frame.mv ?? frame.view ?? null
      : frame.view ?? null;

  const widthsOf = (index: number) => frames[index].widths ?? guide.widths;

  const urlFor = (index: number) => {
    const need =
      stage.clientWidth *
      Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO) *
      Math.max(
        1,
        fitView(viewOf(frames[index]), stage.clientWidth, stage.clientHeight)
          .scale
      );
    return frameUrl(index, pickFrameWidth(widthsOf(index), need));
  };

  /** 그림 한 장 — 받고 해독까지 끝낸 뒤 돌려준다. 순간 끊김에 대비해 간격을 두고 세 번까지 받고, 끝내 실패하면 다음 조작 때 다시 */
  const preload = (
    index: number,
    urgent = false
  ): Promise<HTMLImageElement | null> => {
    if (index < 0 || index >= count) return Promise.resolve(null);
    const url = urlFor(index);
    const cached = loaded.get(index);
    if (cached?.url === url) return cached.image;
    const attempt = (n: number) =>
      new Promise<HTMLImageElement | null>((resolve) => {
        const next = new Image();
        next.decoding = "async";
        if (urgent) next.fetchPriority = "high";
        next.onload = () =>
          (typeof next.decode === "function"
            ? next.decode().catch(() => undefined)
            : Promise.resolve()
          ).then(() => resolve(next));
        next.onerror = () => resolve(null);
        next.src = n ? `${url}${url.includes("?") ? "&" : "?"}r=${n}` : url;
      });
    const pending = (async () => {
      for (let n = 0; n < LOAD_TRIES; n++) {
        const result = await attempt(n);
        if (result) return result;
        await wait(RETRY_GAP_MS * (n + 1));
      }
      loaded.delete(index);
      return null;
    })();
    loaded.set(index, { url, image: pending });
    return pending;
  };

  const preloadAhead = (index: number) => {
    void preload((index + 1) % count);
    if (count > 2) void preload((index + 2) % count);
  };

  /** 메모리: 지금 장 앞뒤(직전 1장 ~ 다음 2장)만 붙들고 나머지는 놓는다(다시 보면 브라우저 저장본에서 바로 해독) */
  const trim = (index: number) => {
    loaded.forEach((_, key) => {
      const distance = (key - index + count) % count;
      if (distance > 2 && distance < count - 1) loaded.delete(key);
    });
  };

  const setCursor = ([x, y]: Point) => {
    cursorAt = [x, y];
    cursor.style.left = `${x}%`;
    cursor.style.top = `${y}%`;
  };

  /**
   * 강조 상자 = 누를 요소의 사각형 + 바깥 여유. 확대 배율과 무관하게 테두리 두께는 화면 기준으로 일정.
   * 자리는 `translate` 로 놓는다 — top/left 는 화소 단위로 맞춰지고 그 어긋남이 확대 배율만큼 커져, 작은 버튼에서 상자가 위로 치우쳐 보였다.
   * 배율은 무대 폭을 소수까지 재서 구한다(확대 층은 무대 폭을 채우는 장면 비율) — 정수로 반올림되는 offset 크기를 쓰면 아래쪽 단추일수록 상자가 밀렸다
   */
  const placeRing = (rect: GuideRect) => {
    const [x, y, w, h] = rect;
    const toLayer = stage.getBoundingClientRect().width / GUIDE_SCENE.width;
    const pad = 4 / scale;
    Object.assign(ring.style, {
      translate: `${x * toLayer - pad}px ${y * toLayer - pad}px`,
      width: `${w * toLayer + pad * 2}px`,
      height: `${h * toLayer + pad * 2}px`,
      borderWidth: `${2.5 / scale}px`,
      borderRadius: `${6 / scale}px`,
      boxShadow: `0 0 0 ${4 / scale}px rgba(29,116,255,.22)`,
    });
  };

  const zoom = (target: GuideRect | null, instant: boolean) => {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (!width) return;
    if (instant) {
      view.style.transition = "none";
      void nextPaint().then(() => {
        view.style.transition = "";
      });
    }
    const kept =
      target && lastView && overlapRatio(lastView, target) > KEEP_VIEW_OVERLAP
        ? lastView
        : target;
    lastView = kept;
    const fit = fitView(kept, width, height);
    scale = fit.scale;
    cursor.style.transform = `scale(${truncateNumber(1 / scale, 3)})`;
    view.style.transform = `translate(${truncateNumber(
      fit.x,
      1
    )}px, ${truncateNumber(fit.y, 1)}px) scale(${truncateNumber(scale, 3)})`;
  };

  const stopFill = () => {
    fill?.cancel();
    fill = null;
  };

  const runFill = (index: number) => {
    const bar = elements.bars()[index];
    if (!bar?.animate) return;
    stopFill();
    fill = bar.animate(
      [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
      {
        duration: stepDuration(frames[index], index >= count - 1),
        easing: "linear",
        fill: "forwards",
      }
    );
  };

  const show = async (index: number, instant: boolean, runToken = token) => {
    const next = await preload(index, true);
    if (runToken !== token || disposed || !next) return;
    const first = !ready;
    // 깜빡임 없이: 새 그림을 위에 겹쳐 서서히 나타낸 뒤 아래 그림을 바꾼다(첫 장면은 흐린 미리보기 위로 바로)
    if (next.src !== image.src) {
      if (instant || first) image.src = next.src;
      else {
        const top = image.cloneNode(false) as HTMLImageElement;
        top.src = next.src;
        top.alt = "";
        top.dataset.next = "";
        view.insertBefore(top, ring);
        await nextPaint();
        top.dataset.on = "";
        await wait(CROSSFADE_MS);
        if (runToken !== token) {
          top.remove();
          return;
        }
        image.src = next.src;
        await wait(SWAP_SETTLE_MS);
        top.remove();
        if (runToken !== token) return;
      }
    }
    current = index;
    const frame = frames[index];
    stopFill();
    onStep(index);
    zoom(viewOf(frame), instant || first);
    delete ring.dataset.on;
    delete cursor.dataset.scroll;
    delete cursor.dataset.down;
    delete cursor.dataset.button;
    if (frame.act) {
      placeRing(frame.act.rect);
      if (instant) {
        ring.dataset.on = "";
        setCursor(centerOf(frame.act.rect));
      }
    }
    if (first) {
      ready = true;
      onReady();
    }
    trim(index);
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      wake = resolve;
      sleepTimer = window.setTimeout(resolve, ms);
    });

  const interrupt = () => {
    clearTimeout(sleepTimer);
    wake?.();
    wake = null;
  };

  const moveCursor = (to: Point, duration: number, runToken: number) => {
    const from = cursorAt;
    const startedAt = performance.now();
    return new Promise<void>((resolve) => {
      const step = (now: number) => {
        if (runToken !== token || !playing) return resolve();
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = easeOutCubic(progress);
        setCursor([
          from[0] + (to[0] - from[0]) * eased,
          from[1] + (to[1] - from[1]) * eased,
        ]);
        if (progress < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
  };

  const play = async (from?: number) => {
    const runToken = ++token;
    playing = true;
    onPlaying(true);
    interrupt();
    const start = from ?? current;
    await show(start, true, runToken);
    if (runToken !== token) return;
    // 다시 볼 때도 누르는 동작이 보이게: 강조 상자는 끄고, 커서는 앞 단계에서 누른 자리(없으면 화면 아래쪽)에서 출발
    delete ring.dataset.on;
    const previousAction = start > 0 ? frames[start - 1].act : undefined;
    setCursor(previousAction ? centerOf(previousAction.rect) : CURSOR_REST);
    const alive = () => playing && runToken === token && !disposed;
    while (alive()) {
      const index = current;
      const frame = frames[index];
      preloadAhead(index);
      runFill(index);
      await sleep(BEAT.look);
      if (!alive()) break;
      const action = frame.act;
      if (action) {
        const gesture = cursorGesture(action);
        // 오른쪽 버튼 표시는 출발부터 놓을 때까지 — 누르는 순간에만 띄우면 놓치기 쉽다
        if (gesture.rightButton) cursor.dataset.button = "right";
        await moveCursor(centerOf(action.rect), BEAT.move, runToken);
        if (!alive()) break;
        ring.dataset.on = "";
        await sleep(BEAT.ring);
        if (!alive()) break;
        if (gesture.scroll) {
          cursor.dataset.scroll = gesture.scroll;
          await sleep(BEAT.press + BEAT.drag);
          delete cursor.dataset.scroll;
          if (!alive()) break;
        } else {
          for (let press = 0; press < gesture.presses; press += 1) {
            if (press > 0) await sleep(BEAT.doubleGap);
            cursor.dataset.down = "";
            await sleep(BEAT.press);
            delete cursor.dataset.down;
          }
          if (!alive()) break;
        }
        if (action.to) {
          cursor.dataset.down = "";
          await moveCursor(centerOf(action.to), BEAT.drag, runToken);
          delete cursor.dataset.down;
          if (!alive()) break;
        }
        delete cursor.dataset.button;
        await sleep(action.kind === "type" ? BEAT.typeHold : BEAT.clickHold);
        if (!alive()) break;
        delete ring.dataset.on;
      } else if (isReviewStep(frame, index >= count - 1)) {
        await sleep(BEAT.review);
        if (!alive()) break;
      }
      if (index >= count - 1) {
        await sleep(BEAT.last);
        if (!alive()) break;
        await show(0, false, runToken);
        continue;
      }
      await show(index + 1, false, runToken);
    }
  };

  const pause = (byUser: boolean) => {
    if (byUser) userPaused = true;
    token++;
    playing = false;
    onPlaying(false);
    interrupt();
    fill?.pause();
  };

  const flashIcon = (kind: "play" | "pause") => {
    flash.dataset.icon = kind;
    delete flash.dataset.on;
    void flash.offsetWidth;
    flash.dataset.on = "";
  };

  const seek = (index: number) => {
    const target = Math.max(0, Math.min(count - 1, index));
    userPaused = false;
    if (reducedMotion) {
      pause(false);
      void show(target, true);
      return;
    }
    void play(target);
  };

  // 크기가 바뀌면(회전·창 크기) 지금 단계의 확대와 강조 상자 자리(그림 층 px)를 다시 맞춘다
  const resizeObserver = new ResizeObserver(() => {
    if (!ready) return;
    const frame = frames[current];
    zoom(viewOf(frame), true);
    if (frame.act) placeRing(frame.act.rect);
  });
  resizeObserver.observe(stage);
  setCursor(CURSOR_REST);

  return {
    setVisible(visible) {
      if (visible) {
        if (!reducedMotion && !userPaused) {
          if (!playing) void play();
        } else if (!playing) void show(current, true);
      } else if (playing) pause(false);
    },
    prefetch() {
      if (stage.clientWidth) void preload(current);
    },
    seek,
    seekBy(delta) {
      seek(current + delta);
    },
    toggle(fromStage) {
      if (playing) {
        pause(true);
        if (fromStage) flashIcon("pause");
      } else {
        userPaused = false;
        void play();
        if (fromStage) flashIcon("play");
      }
    },
    peekUrl(index) {
      return loaded.get(index)?.url ?? frameUrl(index, widthsOf(index)[0]);
    },
    dispose() {
      disposed = true;
      token++;
      playing = false;
      interrupt();
      stopFill();
      resizeObserver.disconnect();
    },
  };
}
